import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createSkill, updateSkill, SKILL_CATEGORIES } from "app/services/skill.service";

const initialSkillData = {
  id: null,
  skillName: "",
  category: "TECHNICAL",
  description: "",
  isActive: true,
};

const AddSkillDialog = ({ open, setOpen, onSuccess, editObject }) => {
  const [data, setData] = useState(initialSkillData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        id: editObject.id,
        skillName: editObject.skillName || "",
        category: editObject.category || "TECHNICAL",
        description: editObject.description || "",
        isActive: editObject.isActive !== undefined ? editObject.isActive : true,
      });
    } else {
      setData(initialSkillData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.skillName || data.skillName.trim() === "") {
      setError("Skill name is required");
      return false;
    }
    if (!data.category) {
      setError("Category is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editObject) {
        await updateSkill(data.id, data);
        setSuccess("Skill updated successfully");
      } else {
        await createSkill(data);
        setSuccess("Skill created successfully");
      }
      setTimeout(() => {
        setData(initialSkillData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialSkillData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Skill" : "Add New Skill"}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="skillName"
            label="Skill Name"
            fullWidth
            variant="outlined"
            value={data.skillName}
            onChange={(e) => setData({ ...data, skillName: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="category"
            label="Category"
            fullWidth
            select
            variant="outlined"
            value={data.category}
            onChange={(e) => setData({ ...data, category: e.target.value })}
            required
          >
            {Object.entries(SKILL_CATEGORIES).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {key.replace("_", " ")}
              </MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <MDBox my={2}>
          <FormControlLabel
            control={
              <Switch
                checked={data.isActive}
                onChange={(e) => setData({ ...data, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddSkillDialog);

